import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetPosicaoCarga } from "~hooks/useSincronizacao";
import { posicaoCargaMock } from "../mocks/new/PosicaoCarga.mock";
import { usarMock } from "../utils/dataSource";


const extractPayloadFromResponse = (response) => {
  if (!response) return null;
  if (response?.data?.data !== undefined) return response.data.data;
  if (response?.data !== undefined) return response.data;
  return response;
};

const extractPayloadFromDexie = (rows) => {
  if (!rows?.length) return null;
  const first = rows[0];
  return first?.payload !== undefined ? first.payload : rows;
};

const persistToCache = async (data) => {
  try {
    await db.posicaoCarga.clear();
    await db.posicaoCarga.add({ payload: data, syncedAt: new Date().toISOString() });
  } catch (err) {
    console.error("Erro ao salvar posição de carga no Dexie:", err);
  }
};


const useLoadPositionData = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const isMock = useMemo(() => {
    return usarMock(posicaoCargaMock);
  }, []);

  const {
    data: queryData,
    isFetching,
    isRefetching,
    isError,
  } = useGetPosicaoCarga(!isMock && navigator.onLine, 0, 0, null);

  const apiPayload = useMemo(() => {
    if (isMock) return posicaoCargaMock;
    return extractPayloadFromResponse(queryData);
  }, [isMock, queryData]);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.posicaoCarga.toArray();
      const extracted = extractPayloadFromDexie(rows);
      if (extracted) setPayload(extracted);
    } catch (error) {
      console.error("Erro ao carregar posição de carga do Dexie:", error);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    if (isMock) {
      setPayload(posicaoCargaMock);
      setLoading(false);
      return;
    }
    loadFromDexie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (isMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    setIsRefreshing(false);

    if (!isError && apiPayload != null) {
      setPayload(apiPayload);
      setLoading(false);
      persistToCache(apiPayload);
    }
  }, [apiPayload, isMock, isError, isFetching, isRefetching]);


  useEffect(() => {
    const handleOffline = () => {
      if (!isMock) {
        setIsRefreshing(false);
        loadFromDexie();
      }
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [isMock, loadFromDexie]);

  return { payload, loading, isRefreshing, isMock };
};

export default useLoadPositionData;
